import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './CustomerSupportChatbot.css';

const QUICK_REPLIES = [
  { label: 'Browse documentation', value: 'docs' },
  { label: 'Contact support', value: 'contact' },
  { label: 'Search articles', value: 'search' },
];

const BOT_RESPONSES = {
  docs: {
    text: "You can browse our documentation from the sidebar on the left, or use the search bar at the top to find specific articles.",
    action: <Link to="/">Go to Home</Link>,
  },
  contact: {
    text: "For direct support, please visit our Contact page. You can reach us at Info@tablemate.net or +267 76020625.",
    action: <Link to="/contact">Contact Us</Link>,
  },
  search: {
    text: "Use the search bar at the top of the page to find articles. You can also browse categories from the sidebar.",
    action: <Link to="/search">Search</Link>,
  },
  default: {
    text: "Thanks for your message! For immediate help, try one of the options below, or visit our Contact page for direct support.",
    action: null,
  },
};

function CustomerSupportChatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hi! 👋 How can I help you today?",
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = (text) => {
    const userMessage = text || inputValue.trim();
    if (!userMessage) return;

    const userMsg = {
      id: Date.now(),
      text: userMessage,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');

    // Simulate bot response
    const lower = userMessage.toLowerCase();
    let response = BOT_RESPONSES.default;
    if (lower.includes('doc') || lower.includes('article') || lower.includes('guide')) {
      response = BOT_RESPONSES.docs;
    } else if (lower.includes('contact') || lower.includes('email') || lower.includes('phone') || lower.includes('support')) {
      response = BOT_RESPONSES.contact;
    } else if (lower.includes('search') || lower.includes('find')) {
      response = BOT_RESPONSES.search;
    }

    setTimeout(() => {
      const botMsg = {
        id: Date.now() + 1,
        text: response.text,
        sender: 'bot',
        timestamp: new Date(),
        action: response.action,
      };
      setMessages((prev) => [...prev, botMsg]);
    }, 600);
  };

  const handleQuickReply = (value) => {
    const label = QUICK_REPLIES.find((r) => r.value === value)?.label || value;
    handleSend(label);
  };

  return (
    <div className="chatbot-container">
      {isOpen && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <h3>Customer Support</h3>
            <button
              className="chatbot-close"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              ×
            </button>
          </div>
          <div className="chatbot-messages">
            {messages.map((msg) => (
              <div key={msg.id} className={`chatbot-message chatbot-message--${msg.sender}`}>
                <div className="chatbot-message-bubble">
                  <p>{msg.text}</p>
                  {msg.action && (
                    <div className="chatbot-message-action">{msg.action}</div>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          <div className="chatbot-quick-replies">
            {QUICK_REPLIES.map((reply) => (
              <button
                key={reply.value}
                className="chatbot-quick-reply"
                onClick={() => handleQuickReply(reply.value)}
              >
                {reply.label}
              </button>
            ))}
          </div>
          <div className="chatbot-input-area">
            <input
              type="text"
              placeholder="Type your question..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            />
            <button
              className="chatbot-send"
              onClick={() => handleSend()}
              aria-label="Send message"
            >
              Send
            </button>
          </div>
        </div>
      )}
      <button
        className="chatbot-toggle"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? '×' : '💬'}
      </button>
    </div>
  );
}

export default CustomerSupportChatbot;
